import { useState } from 'react';
import { Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import useFetchAgencyDnsSettings from 'components/domain/customDomains/useFetchAgencyDnsSettings';
import { BSCheckboxStyled } from 'components/forms/checkboxField/CheckboxField.styles';
import Loader from 'components/loader/Loader';
import { DbsWhiteLabelSettings } from 'models/AgencyDbs';
import {
  ApplicationDnsSetting,
  WhitelabelSubjectApplication,
} from 'models/CustomDomains';
import useFetchDbsSettings from '../useFetchDbsSettings';
import {
  WhiteLabelBodyStyled,
  WhiteLabelCardStyled,
  WhiteLabelIntroStyled,
  WhiteLabelToggleStyled,
} from './WhiteLabelSettings.styles';
import { WHITE_LABEL_HELP_URL } from './WhiteLabelSettings.constants';
import BookingCustomDomainSection from './sections/BookingCustomDomainSection';
import FaviconField from './sections/FaviconField';
import useFetchDbsWhiteLabelSettings from './useFetchDbsWhiteLabelSettings';
import useUpdateWhiteLabelEnabled from './useUpdateWhiteLabelEnabled';

/**
 * The checkbox both reveals the settings and persists the agency's white label flag. Mounted
 * only once everything is loaded so the saved value is read once — cancelling the domain
 * later must not collapse it.
 */
const CustomDomainSettings = ({
  dbsSetting,
  isCheckingConfiguration,
  isWhiteLabelEnabled,
  onCheckConfiguration,
  whiteLabelSettings,
}: {
  dbsSetting?: ApplicationDnsSetting;
  isCheckingConfiguration: boolean;
  isWhiteLabelEnabled: boolean;
  onCheckConfiguration: () => void;
  whiteLabelSettings?: DbsWhiteLabelSettings;
}) => {
  const { t } = useTranslation();
  const [isEnabled, setIsEnabled] = useState(isWhiteLabelEnabled);
  const { updateWhiteLabelEnabled, isSavingWhiteLabelEnabled } =
    useUpdateWhiteLabelEnabled();

  // shown right away, then put back if the save fails, so the box tracks what is stored
  const handleToggle = () => {
    const nextEnabled = !isEnabled;

    setIsEnabled(nextEnabled);
    updateWhiteLabelEnabled(nextEnabled).catch(() => setIsEnabled(isEnabled));
  };

  return (
    <>
      <WhiteLabelToggleStyled>
        <BSCheckboxStyled
          checked={isEnabled}
          data-testid="enabled"
          disabled={isSavingWhiteLabelEnabled}
          id="enabled"
          onChange={handleToggle}
        >
          {t('pageDirectBookingEngine.whiteLabelSettings.enabled')}
        </BSCheckboxStyled>
      </WhiteLabelToggleStyled>
      {isEnabled && (
        <div data-testid="white-label-settings-fields">
          <FaviconField
            faviconUrl={whiteLabelSettings?.faviconUrl}
            label={t('pageDirectBookingEngine.whiteLabelSettings.favicon')}
            tooltip={t(
              'pageDirectBookingEngine.whiteLabelSettings.faviconTooltip',
            )}
          />
          <BookingCustomDomainSection
            dnsSetting={dbsSetting}
            isCheckingConfiguration={isCheckingConfiguration}
            onCheckConfiguration={onCheckConfiguration}
          />
        </div>
      )}
    </>
  );
};

CustomDomainSettings.defaultProps = {
  dbsSetting: undefined,
  whiteLabelSettings: undefined,
};

// Built only on what pmp-core exposes today: the dns-settings API for the domain, the
// favicon upload, and the booking site's own payload for the saved favicon.
const WhiteLabelSettingsTab = () => {
  const { t } = useTranslation();
  const {
    dnsSettings,
    isLoading: isDnsLoading,
    isFetching,
    isError: isDnsError,
    refetch,
  } = useFetchAgencyDnsSettings();
  // a failure here only costs the favicon preview, so it degrades instead of alerting
  const { whiteLabelSettings, isLoading: isWhiteLabelLoading } =
    useFetchDbsWhiteLabelSettings();
  const { dbsSettings, isLoading: isDbsSettingsLoading } =
    useFetchDbsSettings();

  if (isDnsLoading || isWhiteLabelLoading || isDbsSettingsLoading) {
    return <Loader />;
  }

  const dbsSetting = dnsSettings.find(
    ({ application }) => application === WhitelabelSubjectApplication.DBS,
  );

  return (
    <WhiteLabelCardStyled data-testid="direct-booking-engine-white-label-settings">
      <WhiteLabelIntroStyled>
        <p data-testid="white-label-settings-intro">
          {t('pageDirectBookingEngine.whiteLabelSettings.intro')}
        </p>
        <p>
          <strong>
            {t('pageDirectBookingEngine.whiteLabelSettings.documentationLabel')}
          </strong>{' '}
          <a
            data-testid="white-label-settings-documentation-link"
            href={WHITE_LABEL_HELP_URL}
            rel="noreferrer"
            target="_blank"
          >
            {t('pageDirectBookingEngine.whiteLabelSettings.documentation')}
          </a>
        </p>
      </WhiteLabelIntroStyled>
      <WhiteLabelBodyStyled>
        {isDnsError && (
          <Alert
            bsStyle="danger"
            data-testid="booking-custom-domain-fetch-error"
          >
            {t('componentCustomDomains.fetchError')}
          </Alert>
        )}
        <CustomDomainSettings
          dbsSetting={dbsSetting}
          isCheckingConfiguration={isFetching}
          isWhiteLabelEnabled={!!dbsSettings?.whiteLabelEnabled}
          onCheckConfiguration={refetch}
          whiteLabelSettings={whiteLabelSettings}
        />
      </WhiteLabelBodyStyled>
    </WhiteLabelCardStyled>
  );
};

export default WhiteLabelSettingsTab;
